import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, Upload, Trash2, MapPin, Building2, Sparkles, Search, CheckSquare, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { LoanOfficerAdManager } from '@/components/admin/LoanOfficerAdManager';

interface LoanOfficerData {
  id?: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  logo_url: string | null;
  company_name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  email: string | null;
  phone: string | null;
  license_state: string | null;
  license_number: string | null;
  bio: string | null;
  is_active: boolean;
  ad_image_url: string | null;
}

interface Site {
  id: string;
  name: string;
  location_served: string;
}

interface Town {
  id: string;
  name: string;
  state: string | null;
  site_id: string;
}

interface Area {
  id: string;
  name: string;
  town_id: string;
  site_id: string;
  town_name?: string;
}

interface Coverage {
  town_ids: string[];
  area_ids: string[];
}

export default function LoanOfficerEdit() {
  const navigate = useNavigate();
  const { officerId } = useParams();
  const isNew = officerId === 'new';
  const { user, loading: authLoading } = useAuth();
  const { isGlobalAdmin, loading: adminLoading, checked: adminChecked } = useGlobalAdmin();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);

  // Coverage filter state
  const [stateFilter, setStateFilter] = useState('');
  const [townFilter, setTownFilter] = useState('');

  const [formData, setFormData] = useState<LoanOfficerData>({
    first_name: '',
    last_name: '',
    photo_url: null,
    logo_url: null,
    company_name: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    email: null,
    phone: null,
    license_state: null,
    license_number: null,
    bio: null,
    is_active: true,
    ad_image_url: null,
  });

  const [sites, setSites] = useState<Site[]>([]);
  const [townsBySite, setTownsBySite] = useState<Record<string, Town[]>>({});
  const [areasBySite, setAreasBySite] = useState<Record<string, Area[]>>({});
  const [allTowns, setAllTowns] = useState<Town[]>([]);
  const [allAreas, setAllAreas] = useState<Area[]>([]);
  const [coverage, setCoverage] = useState<Coverage>({ town_ids: [], area_ids: [] });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // Redirect non-global admins - wait for admin check to complete
  useEffect(() => {
    if (!adminChecked) return;
    
    if (user && !isGlobalAdmin) {
      toast({ title: 'Access Denied', description: 'Only global admins can manage loan officers', variant: 'destructive' });
      navigate('/admin');
    }
  }, [user, adminChecked, isGlobalAdmin, navigate, toast]);

  useEffect(() => {
    if (isGlobalAdmin) {
      fetchData();
    }
  }, [isGlobalAdmin, officerId]);

  // Get unique states from all towns
  const uniqueStates = useMemo(() => {
    const states = new Set<string>();
    allTowns.forEach(town => {
      if (town.state) states.add(town.state);
    });
    return Array.from(states).sort();
  }, [allTowns]);

  // Filter sites based on state and town filters
  const filteredSites = useMemo(() => {
    if (!stateFilter && !townFilter) return sites;

    return sites.filter(site => {
      const siteTowns = townsBySite[site.id] || [];
      
      // Check if any town in this site matches the filters
      return siteTowns.some(town => {
        const matchesState = !stateFilter || town.state === stateFilter;
        const matchesTownSearch = !townFilter || 
          town.name.toLowerCase().includes(townFilter.toLowerCase());
        return matchesState && matchesTownSearch;
      });
    });
  }, [sites, townsBySite, stateFilter, townFilter]);

  // Get filtered towns for a site
  const getFilteredTowns = (siteId: string) => {
    const siteTowns = townsBySite[siteId] || [];
    if (!stateFilter && !townFilter) return siteTowns;
    
    return siteTowns.filter(town => {
      const matchesState = !stateFilter || town.state === stateFilter;
      const matchesTownSearch = !townFilter || 
        town.name.toLowerCase().includes(townFilter.toLowerCase());
      return matchesState && matchesTownSearch;
    });
  };

  // Get filtered areas for a site (areas under filtered towns)
  const getFilteredAreas = (siteId: string) => {
    const siteAreas = areasBySite[siteId] || [];
    if (!stateFilter && !townFilter) return siteAreas;
    
    const filteredTownIds = new Set(getFilteredTowns(siteId).map(t => t.id));
    return siteAreas.filter(area => filteredTownIds.has(area.town_id));
  };

  const fetchData = async () => {
    setLoading(true);

    // Fetch all sites
    const { data: sitesData } = await supabase
      .from('sites')
      .select('id, name, location_served')
      .eq('status', 'active')
      .order('name');
    setSites(sitesData || []);

    // Fetch all published towns
    const { data: townsData } = await supabase
      .from('towns')
      .select('id, name, state, site_id')
      .eq('is_published', true)
      .order('name');

    setAllTowns(townsData || []);

    // Group towns by site
    const townGroups: Record<string, Town[]> = {};
    (townsData || []).forEach(town => {
      if (!townGroups[town.site_id]) townGroups[town.site_id] = [];
      townGroups[town.site_id].push(town);
    });
    setTownsBySite(townGroups);

    // Fetch all published areas
    const { data: areasData } = await supabase
      .from('areas')
      .select('id, name, town_id, site_id')
      .eq('is_published', true)
      .order('name');

    setAllAreas(areasData || []);

    // Group areas by site with town names
    const areaGroups: Record<string, Area[]> = {};
    (areasData || []).forEach(area => {
      const town = townsData?.find(t => t.id === area.town_id);
      const areaWithTown = { ...area, town_name: town?.name || 'Unknown' };
      if (!areaGroups[area.site_id]) areaGroups[area.site_id] = [];
      areaGroups[area.site_id].push(areaWithTown);
    });
    setAreasBySite(areaGroups);

    // Fetch loan officer data if editing
    if (!isNew && officerId) {
      const { data: officerData, error } = await supabase
        .from('loan_officers')
        .select('*')
        .eq('id', officerId)
        .single();

      if (error || !officerData) {
        toast({ title: 'Error', description: 'Loan officer not found', variant: 'destructive' });
        navigate('/admin/loan-officers');
        return;
      }

      setFormData({
        id: officerData.id,
        first_name: officerData.first_name,
        last_name: officerData.last_name,
        photo_url: officerData.photo_url,
        logo_url: officerData.logo_url,
        company_name: officerData.company_name,
        address: officerData.address,
        city: officerData.city,
        state: officerData.state,
        zip: officerData.zip,
        email: officerData.email,
        phone: officerData.phone,
        license_state: officerData.license_state,
        license_number: officerData.license_number,
        bio: officerData.bio,
        is_active: officerData.is_active,
        ad_image_url: officerData.ad_image_url,
      });

      // Fetch coverage
      const { data: coverageData } = await supabase
        .from('loan_officer_coverage')
        .select('town_id, area_id')
        .eq('loan_officer_id', officerId);

      if (coverageData) {
        setCoverage({
          town_ids: coverageData.filter(c => c.town_id).map(c => c.town_id!),
          area_ids: coverageData.filter(c => c.area_id).map(c => c.area_id!),
        });
      }
    }

    setLoading(false);
  };

  const handleInputChange = (field: keyof LoanOfficerData, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (file: File, type: 'photo' | 'logo') => {
    setUploading(true);

    const fileExt = file.name.split('.').pop();
    const fileName = `loan-officers/${type}_${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('realtor-assets')
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
      setUploading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('realtor-assets')
      .getPublicUrl(fileName);

    const field = type === 'photo' ? 'photo_url' : 'logo_url';
    setFormData(prev => ({ ...prev, [field]: publicUrl }));
    setUploading(false);
    toast({ title: 'Success', description: 'Image uploaded successfully' });
  };

  const handleGenerateBio = async () => {
    if (!formData.first_name) {
      toast({ title: 'Missing Info', description: 'Please enter at least a first name to generate a bio', variant: 'destructive' });
      return;
    }

    setGeneratingBio(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-loan-officer-bio', {
        body: {
          firstName: formData.first_name,
          lastName: formData.last_name,
          companyName: formData.company_name,
          phone: formData.phone,
          email: formData.email,
          licenseNumber: formData.license_number,
          licenseState: formData.license_state,
        },
      });

      if (error) throw error;

      if (data?.bio) {
        setFormData(prev => ({ ...prev, bio: data.bio }));
        toast({ title: 'Success', description: 'Bio generated successfully' });
      } else if (data?.error) {
        throw new Error(data.error);
      }
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to generate bio', 
        variant: 'destructive' 
      });
    } finally {
      setGeneratingBio(false);
    }
  };

  const handleTownToggle = (townId: string, checked: boolean) => {
    setCoverage(prev => {
      const newTownIds = checked
        ? [...prev.town_ids, townId]
        : prev.town_ids.filter(id => id !== townId);

      // When a town is selected, auto-select its areas
      let newAreaIds = [...prev.area_ids];
      if (checked) {
        const townAreas = allAreas.filter(a => a.town_id === townId);
        townAreas.forEach(area => {
          if (!newAreaIds.includes(area.id)) {
            newAreaIds.push(area.id);
          }
        });
      }

      return { town_ids: newTownIds, area_ids: newAreaIds };
    });
  };

  const handleAreaToggle = (areaId: string, checked: boolean) => {
    setCoverage(prev => ({
      ...prev,
      area_ids: checked
        ? [...prev.area_ids, areaId]
        : prev.area_ids.filter(id => id !== areaId),
    }));
  };

  const handleSelectAllTowns = (siteId: string) => {
    const siteTowns = getFilteredTowns(siteId);
    const siteAreas = getFilteredAreas(siteId);
    
    setCoverage(prev => {
      const newTownIds = [...prev.town_ids];
      const newAreaIds = [...prev.area_ids];

      siteTowns.forEach(town => {
        if (!newTownIds.includes(town.id)) {
          newTownIds.push(town.id);
        }
      });

      // Also select all areas under these towns
      siteAreas.forEach(area => {
        if (!newAreaIds.includes(area.id)) {
          newAreaIds.push(area.id);
        }
      });

      return { town_ids: newTownIds, area_ids: newAreaIds };
    });

    toast({ title: 'Selected', description: `All towns and areas for this site have been selected` });
  };

  const handleDeselectAllTowns = (siteId: string) => {
    const siteTowns = getFilteredTowns(siteId);
    const siteAreas = getFilteredAreas(siteId);
    const townIdsToRemove = new Set(siteTowns.map(t => t.id));
    const areaIdsToRemove = new Set(siteAreas.map(a => a.id));

    setCoverage(prev => ({
      town_ids: prev.town_ids.filter(id => !townIdsToRemove.has(id)),
      area_ids: prev.area_ids.filter(id => !areaIdsToRemove.has(id)),
    }));
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast({ title: 'Error', description: 'First and last name are required', variant: 'destructive' });
      return;
    }

    setSaving(true);

    try {
      let loanOfficerId = officerId;

      if (isNew) {
        const { data, error } = await supabase
          .from('loan_officers')
          .insert([{
            site_id: null as any, // site_id is now optional for cross-site officers
            first_name: formData.first_name,
            last_name: formData.last_name,
            photo_url: formData.photo_url,
            logo_url: formData.logo_url,
            company_name: formData.company_name,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            email: formData.email,
            phone: formData.phone,
            license_state: formData.license_state,
            license_number: formData.license_number,
            bio: formData.bio,
            is_active: formData.is_active,
            ad_image_url: formData.ad_image_url,
          }])
          .select('id')
          .single();

        if (error) throw error;
        loanOfficerId = data.id;
      } else {
        const { error } = await supabase
          .from('loan_officers')
          .update({
            first_name: formData.first_name,
            last_name: formData.last_name,
            photo_url: formData.photo_url,
            logo_url: formData.logo_url,
            company_name: formData.company_name,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            email: formData.email,
            phone: formData.phone,
            license_state: formData.license_state,
            license_number: formData.license_number,
            bio: formData.bio,
            is_active: formData.is_active,
            ad_image_url: formData.ad_image_url,
          })
          .eq('id', officerId!);

        if (error) throw error;
      }

      // Save coverage - delete existing and insert new
      if (loanOfficerId) {
        await supabase
          .from('loan_officer_coverage')
          .delete()
          .eq('loan_officer_id', loanOfficerId);

        const coverageInserts = [
          ...coverage.town_ids.map(town_id => ({
            loan_officer_id: loanOfficerId!,
            town_id,
            area_id: null,
          })),
          ...coverage.area_ids.map(area_id => ({
            loan_officer_id: loanOfficerId!,
            town_id: null,
            area_id,
          })),
        ];

        if (coverageInserts.length > 0) {
          const { error: coverageError } = await supabase
            .from('loan_officer_coverage')
            .insert(coverageInserts);

          if (coverageError) throw coverageError;
        }
      }

      toast({ title: 'Success', description: 'Loan officer saved successfully', duration: 3000 });
      navigate('/admin/loan-officers');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!officerId || isNew) return;
    
    if (!confirm('Are you sure you want to delete this loan officer?')) return;

    const { error } = await supabase
      .from('loan_officers')
      .delete()
      .eq('id', officerId);

    if (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Loan officer deleted' });
      navigate('/admin/loan-officers');
    }
  };

  if (authLoading || adminLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isGlobalAdmin) {
    return null;
  }

  const totalCoverage = coverage.town_ids.length + coverage.area_ids.length;

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin/loan-officers" className="text-muted-foreground hover:text-foreground">
              ← Back to Loan Officers
            </Link>
            <div className="flex items-center gap-2">
              {!isNew && (
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-6">
          {isNew ? 'Add New Loan Officer' : `Edit ${formData.first_name} ${formData.last_name}`}
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo & Logo */}
            <Card>
              <CardHeader>
                <CardTitle>Photos</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-8">
                <div className="text-center">
                  <Avatar className="h-24 w-24 mx-auto mb-3 rounded-md">
                    <AvatarImage src={formData.photo_url || undefined} className="rounded-md object-top" />
                    <AvatarFallback className="rounded-md">{formData.first_name?.[0]}{formData.last_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <Label htmlFor="photo-upload" className="cursor-pointer">
                    <div className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Upload Photo'}
                    </div>
                    <Input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'photo')}
                    />
                  </Label>
                </div>
                <div className="text-center">
                  <div className="h-24 w-24 mx-auto mb-3 border rounded-lg flex items-center justify-center bg-muted">
                    {formData.logo_url ? (
                      <img src={formData.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
                    ) : (
                      <Building2 className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                    </div>
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'logo')}
                    />
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Status */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_active">Active</Label>
                    <p className="text-sm text-muted-foreground">
                      Only active loan officers appear on public pages
                    </p>
                  </div>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Personal Info */}
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value || null)}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value || null)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name || ''}
                    onChange={(e) => handleInputChange('company_name', e.target.value || null)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card>
              <CardHeader>
                <CardTitle>Address</CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value || null)}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value || null)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state || ''}
                      onChange={(e) => handleInputChange('state', e.target.value || null)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip">ZIP</Label>
                    <Input
                      id="zip"
                      value={formData.zip || ''}
                      onChange={(e) => handleInputChange('zip', e.target.value || null)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* License Info */}
            <Card>
              <CardHeader>
                <CardTitle>License Information</CardTitle>
                <CardDescription>
                  Enter multiple licenses separated by commas (e.g., "MA #12345, NH #67890, NMLS #123456")
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="license_number">Licenses</Label>
                  <Input
                    id="license_number"
                    placeholder="e.g., MA #12345, NMLS #123456"
                    value={formData.license_number || ''}
                    onChange={(e) => handleInputChange('license_number', e.target.value || null)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Include state abbreviations and license numbers. For NMLS, use "NMLS #" prefix.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Bio */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Bio</CardTitle>
                    <CardDescription>Tell potential clients about this loan officer</CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleGenerateBio}
                    disabled={generatingBio}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {generatingBio ? 'Generating...' : 'Generate Bio'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  id="bio"
                  rows={6}
                  value={formData.bio || ''}
                  onChange={(e) => handleInputChange('bio', e.target.value || null)}
                  placeholder="Write a brief bio about this loan officer..."
                />
              </CardContent>
            </Card>

            {/* Loan Officer Ad */}
            {!isNew && officerId && (
              <LoanOfficerAdManager
                loanOfficerId={officerId}
                officerName={`${formData.first_name} ${formData.last_name}`.trim()}
                nmls={formData.license_number || ''}
                photoUrl={formData.photo_url}
                logoUrl={formData.logo_url}
                adImageUrl={formData.ad_image_url}
                onAdImageChange={(url) => handleInputChange('ad_image_url', url)}
              />
            )}
          </div>

          {/* Right Column - Coverage */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Coverage Areas
                </CardTitle>
                <CardDescription>
                  Select towns and neighborhoods across all sites where this loan officer will appear.
                  <span className="block mt-1 font-medium text-foreground">
                    {totalCoverage} area{totalCoverage !== 1 ? 's' : ''} selected
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="space-y-3 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filter Sites</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="state-filter" className="text-xs">State</Label>
                      <select
                        id="state-filter"
                        value={stateFilter}
                        onChange={(e) => setStateFilter(e.target.value)}
                        className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                      >
                        <option value="">All States</option>
                        {uniqueStates.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="town-filter" className="text-xs">Town</Label>
                      <Input
                        id="town-filter"
                        placeholder="Search..."
                        value={townFilter}
                        onChange={(e) => setTownFilter(e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  {(stateFilter || townFilter) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setStateFilter(''); setTownFilter(''); }}
                      className="text-xs"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>

                {filteredSites.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {sites.length === 0 ? 'No sites available' : 'No sites match your filters'}
                  </p>
                ) : (
                  <Accordion type="multiple" className="w-full">
                    {filteredSites.map((site) => {
                      const siteTowns = getFilteredTowns(site.id);
                      const siteAreas = getFilteredAreas(site.id);
                      const hasCoverage = siteTowns.length > 0 || siteAreas.length > 0;
                      const selectedTownCount = siteTowns.filter(t => coverage.town_ids.includes(t.id)).length;
                      const selectedAreaCount = siteAreas.filter(a => coverage.area_ids.includes(a.id)).length;
                      const selectedCount = selectedTownCount + selectedAreaCount;
                      const allTownsSelected = siteTowns.length > 0 && selectedTownCount === siteTowns.length;

                      if (!hasCoverage) return null;

                      return (
                        <AccordionItem key={site.id} value={site.id}>
                          <AccordionTrigger className="text-sm">
                            <div className="flex items-center gap-2">
                              <span>{site.name}</span>
                              {selectedCount > 0 && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                                  {selectedCount}
                                </span>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="space-y-4">
                            {/* Towns */}
                            {siteTowns.length > 0 && (
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="text-xs font-medium text-muted-foreground">TOWNS</h5>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-xs"
                                    onClick={() => allTownsSelected 
                                      ? handleDeselectAllTowns(site.id) 
                                      : handleSelectAllTowns(site.id)
                                    }
                                  >
                                    <CheckSquare className="h-3 w-3 mr-1" />
                                    {allTownsSelected ? 'Deselect All' : 'Select All'}
                                  </Button>
                                </div>
                                <div className="space-y-1">
                                  {siteTowns.map((town) => (
                                    <div key={town.id} className="flex items-center gap-2">
                                      <Checkbox
                                        id={`town-${town.id}`}
                                        checked={coverage.town_ids.includes(town.id)}
                                        onCheckedChange={(checked) => handleTownToggle(town.id, checked as boolean)}
                                      />
                                      <Label htmlFor={`town-${town.id}`} className="text-sm font-normal cursor-pointer">
                                        {town.name}{town.state ? `, ${town.state}` : ''}
                                      </Label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Areas */}
                            {siteAreas.length > 0 && (
                              <div>
                                <h5 className="text-xs font-medium text-muted-foreground mb-2">NEIGHBORHOODS</h5>
                                <div className="space-y-1">
                                  {siteAreas.map((area) => (
                                    <div key={area.id} className="flex items-center gap-2">
                                      <Checkbox
                                        id={`area-${area.id}`}
                                        checked={coverage.area_ids.includes(area.id)}
                                        onCheckedChange={(checked) => handleAreaToggle(area.id, checked as boolean)}
                                      />
                                      <Label htmlFor={`area-${area.id}`} className="text-sm font-normal cursor-pointer">
                                        {area.name}
                                        <span className="text-muted-foreground ml-1">({area.town_name})</span>
                                      </Label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
